import { useCallback, useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import type { Control, UseFormGetValues, UseFormReset } from 'react-hook-form';

import { propertyAdEditorProvider } from '../../providers/propertyAdEditorProvider';
import {
  PROPERTY_AD_IN_PROGRESS_STATUS,
  type PropertyAdEditorAutosaveValues,
  type PropertyAdEditorFormValues,
  type PropertyAdInProgressAutosaveFields,
  type PropertyAdInProgressWorkspace,
} from '../../types/propertyAdEditor';
import type { ResponseType } from '../../types/responses';

import { mapWorkspaceToEditorForm } from './propertyAdEditorForm';
import {
  buildPropertyAdEditorAutosaveDiff,
  clonePropertyAdEditorAutosaveValues,
  getPropertyAdEditorAutosaveValues,
  getPropertyAdEditorPersistedAutosaveValues,
  hasPropertyAdEditorAutosaveDiff,
} from './propertyAdEditorAutosave';

export const PROPERTY_AD_EDITOR_AUTOSAVE_DELAY_MS = 1000;

export type PropertyAdEditorAutosaveState =
  | { status: 'disabled' }
  | { status: 'saved' }
  | { status: 'saving' }
  | { status: 'error' }
  | { status: 'conflict'; canReload: boolean };

const areDefaultFieldsAcknowledged = (
  fields: PropertyAdInProgressAutosaveFields,
  saved: PropertyAdInProgressWorkspace
) =>
  (!('currency' in fields) || fields.currency === (saved.currency ?? null)) &&
  (!('currencyCommission' in fields) ||
    fields.currencyCommission === (saved.currencyCommission ?? null)) &&
  (!('completionStatus' in fields) ||
    fields.completionStatus === (saved.completionStatus ?? null)) &&
  (!('postHandover' in fields) || fields.postHandover === (saved.postHandover ?? null)) &&
  (!('handover' in fields) ||
    (fields.handover === null
      ? saved.handover == null
      : saved.handover != null &&
        fields.handover?.quarter === saved.handover.quarter &&
        fields.handover?.year === saved.handover.year));

interface UsePropertyAdEditorAutosaveOptions {
  control: Control<PropertyAdEditorFormValues>;
  getValues: UseFormGetValues<PropertyAdEditorFormValues>;
  initialValues: PropertyAdEditorFormValues;
  responsibleDefaultUserId?: string;
  reset: UseFormReset<PropertyAdEditorFormValues>;
  workspace: Pick<
    PropertyAdInProgressWorkspace,
    'completionStatus' | 'currency' | 'currencyCommission' | 'id' | 'multilisting' | 'version'
  > & {
    status: string;
  };
}

export const usePropertyAdEditorAutosave = ({
  control,
  getValues,
  initialValues,
  responsibleDefaultUserId,
  reset,
  workspace,
}: UsePropertyAdEditorAutosaveOptions) => {
  const autosaveEnabled = workspace.status === PROPERTY_AD_IN_PROGRESS_STATUS;
  const watchedValues = useWatch({ control });
  const [state, setState] = useState<PropertyAdEditorAutosaveState>(
    autosaveEnabled ? { status: 'saved' } : { status: 'disabled' }
  );
  const lastSavedValuesRef = useRef<PropertyAdEditorAutosaveValues>(
    getPropertyAdEditorPersistedAutosaveValues(workspace, initialValues)
  );
  const hydratedValuesRef = useRef(getPropertyAdEditorAutosaveValues(initialValues));
  const serverVersionRef = useRef(workspace.version);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestInFlightRef = useRef(false);
  const requestFailedRef = useRef(false);
  const requestWaitersRef = useRef<Array<() => void>>([]);
  const conflictRef = useRef(false);
  const conflictRecoveryStartedRef = useRef(false);
  const conflictWorkspaceRef = useRef<PropertyAdInProgressWorkspace | null>(null);
  const hydratingRef = useRef(true);
  const mountedRef = useRef(true);
  const workspaceGenerationRef = useRef(0);
  const autosaveEnabledRef = useRef(autosaveEnabled);
  const runAutosaveRef = useRef<() => Promise<void>>(async () => undefined);

  const resolveRequestWaiters = useCallback(() => {
    const waiters = requestWaitersRef.current.splice(0);
    waiters.forEach((resolve) => resolve());
  }, []);

  const waitForInFlightRequest = useCallback(
    () =>
      requestInFlightRef.current
        ? new Promise<void>((resolve) => requestWaitersRef.current.push(resolve))
        : Promise.resolve(),
    []
  );

  const clearDebounce = useCallback(() => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const getCurrentValues = useCallback(
    () => getPropertyAdEditorAutosaveValues(getValues()),
    [getValues]
  );

  const hasPendingChanges = useCallback(
    (currentValues = getCurrentValues()) =>
      hasPropertyAdEditorAutosaveDiff(
        buildPropertyAdEditorAutosaveDiff(
          currentValues,
          lastSavedValuesRef.current,
          responsibleDefaultUserId
        )
      ),
    [getCurrentValues, responsibleDefaultUserId]
  );

  const recoverConflictWorkspace = useCallback(async (requestGeneration: number) => {
    if (conflictRecoveryStartedRef.current) return;

    conflictRecoveryStartedRef.current = true;

    try {
      const response = await propertyAdEditorProvider.getInProgressWorkspace();

      if (
        !mountedRef.current ||
        workspaceGenerationRef.current !== requestGeneration ||
        !conflictRef.current
      ) {
        return;
      }

      if (response.ok && response.data) {
        conflictWorkspaceRef.current = response.data;
        setState({ status: 'conflict', canReload: true });
      }
    } catch {
      // The conflict stays persistent. Recovery GET is intentionally attempted only once.
    }
  }, []);

  const runAutosave = useCallback(async () => {
    if (
      !mountedRef.current ||
      !autosaveEnabledRef.current ||
      hydratingRef.current ||
      conflictRef.current ||
      requestInFlightRef.current
    ) {
      return;
    }

    clearDebounce();

    const requestValues = getCurrentValues();
    const fields = buildPropertyAdEditorAutosaveDiff(
      requestValues,
      lastSavedValuesRef.current,
      responsibleDefaultUserId
    );

    if (!hasPropertyAdEditorAutosaveDiff(fields)) {
      requestFailedRef.current = false;
      setState({ status: 'saved' });
      return;
    }

    const requestGeneration = workspaceGenerationRef.current;
    const expectedVersion = serverVersionRef.current;
    requestInFlightRef.current = true;
    requestFailedRef.current = false;
    setState({ status: 'saving' });

    let response: ResponseType<PropertyAdInProgressWorkspace>;

    try {
      response = await propertyAdEditorProvider.autosaveInProgressWorkspace({
        expectedVersion,
        ...fields,
      });
    } catch {
      requestInFlightRef.current = false;
      requestFailedRef.current = true;
      resolveRequestWaiters();

      if (mountedRef.current && workspaceGenerationRef.current === requestGeneration) {
        setState({ status: 'error' });
      }
      return;
    }

    requestInFlightRef.current = false;
    resolveRequestWaiters();

    if (!mountedRef.current) return;

    if (workspaceGenerationRef.current !== requestGeneration) {
      if (
        autosaveEnabledRef.current &&
        !hydratingRef.current &&
        !conflictRef.current &&
        hasPendingChanges()
      ) {
        void runAutosaveRef.current();
      }
      return;
    }

    if (response.status === 409) {
      requestFailedRef.current = true;
      conflictRef.current = true;
      clearDebounce();
      setState({ status: 'conflict', canReload: false });
      await recoverConflictWorkspace(requestGeneration);
      return;
    }

    if (!response.ok || !response.data) {
      requestFailedRef.current = true;
      setState({ status: 'error' });
      return;
    }

    serverVersionRef.current = response.data.version;

    if (!areDefaultFieldsAcknowledged(fields, response.data)) {
      requestFailedRef.current = true;
      setState({ status: 'error' });
      return;
    }

    requestFailedRef.current = false;
    lastSavedValuesRef.current = clonePropertyAdEditorAutosaveValues(requestValues);

    if (hasPendingChanges()) {
      void runAutosaveRef.current();
      return;
    }

    setState({ status: 'saved' });
  }, [
    clearDebounce,
    getCurrentValues,
    hasPendingChanges,
    recoverConflictWorkspace,
    responsibleDefaultUserId,
    resolveRequestWaiters,
  ]);

  useEffect(() => {
    runAutosaveRef.current = runAutosave;
  }, [runAutosave]);

  useEffect(() => {
    workspaceGenerationRef.current += 1;
    autosaveEnabledRef.current = autosaveEnabled;
    hydratingRef.current = true;
    conflictRef.current = false;
    conflictRecoveryStartedRef.current = false;
    conflictWorkspaceRef.current = null;
    requestFailedRef.current = false;
    lastSavedValuesRef.current = getPropertyAdEditorPersistedAutosaveValues(
      workspace,
      initialValues
    );
    hydratedValuesRef.current = getPropertyAdEditorAutosaveValues(initialValues);
    serverVersionRef.current = workspace.version;
    clearDebounce();
    setState(autosaveEnabled ? { status: 'saved' } : { status: 'disabled' });
  }, [
    autosaveEnabled,
    clearDebounce,
    initialValues,
    workspace.completionStatus,
    workspace.id,
    workspace.currency,
    workspace.currencyCommission,
    workspace.multilisting,
    workspace.version,
  ]);

  useEffect(() => {
    const currentValues = getCurrentValues();

    if (hydratingRef.current) {
      if (
        !hasPropertyAdEditorAutosaveDiff(
          buildPropertyAdEditorAutosaveDiff(currentValues, hydratedValuesRef.current)
        )
      ) {
        hydratingRef.current = false;
      }
      return;
    }

    clearDebounce();

    if (!autosaveEnabledRef.current || conflictRef.current) return;

    if (!hasPendingChanges(currentValues)) {
      if (!requestInFlightRef.current) setState({ status: 'saved' });
      return;
    }

    if (requestInFlightRef.current) return;

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      void runAutosaveRef.current();
    }, PROPERTY_AD_EDITOR_AUTOSAVE_DELAY_MS);
  }, [clearDebounce, getCurrentValues, hasPendingChanges, watchedValues]);

  useEffect(
    () => () => {
      mountedRef.current = false;
      clearDebounce();
      resolveRequestWaiters();
    },
    [clearDebounce, resolveRequestWaiters]
  );

  const flush = useCallback(async (): Promise<boolean> => {
    clearDebounce();

    if (!mountedRef.current || !autosaveEnabledRef.current || conflictRef.current) return false;

    while (mountedRef.current && autosaveEnabledRef.current) {
      if (requestInFlightRef.current) {
        await waitForInFlightRequest();
        if (requestFailedRef.current || conflictRef.current) return false;
        continue;
      }

      const currentValues = getCurrentValues();

      if (hydratingRef.current) {
        hydratingRef.current = false;
      }

      if (!hasPendingChanges(currentValues)) {
        return true;
      }

      requestFailedRef.current = false;
      await runAutosaveRef.current();

      if (requestFailedRef.current || conflictRef.current) return false;
    }

    return false;
  }, [clearDebounce, getCurrentValues, hasPendingChanges, waitForInFlightRequest]);

  const stopAutosave = useCallback(() => {
    autosaveEnabledRef.current = false;
    clearDebounce();
  }, [clearDebounce]);

  const retry = useCallback(() => {
    if (!autosaveEnabledRef.current || conflictRef.current) return;

    clearDebounce();
    void runAutosaveRef.current();
  }, [clearDebounce]);

  const reloadSavedVersion = useCallback(() => {
    const serverWorkspace = conflictWorkspaceRef.current;
    if (!serverWorkspace) return;

    const serverValues = mapWorkspaceToEditorForm(serverWorkspace, responsibleDefaultUserId);
    workspaceGenerationRef.current += 1;
    clearDebounce();
    hydratingRef.current = true;
    conflictRef.current = false;
    conflictRecoveryStartedRef.current = false;
    conflictWorkspaceRef.current = null;
    lastSavedValuesRef.current = getPropertyAdEditorPersistedAutosaveValues(
      serverWorkspace,
      serverValues
    );
    hydratedValuesRef.current = getPropertyAdEditorAutosaveValues(serverValues);
    serverVersionRef.current = serverWorkspace.version;
    reset(serverValues);
    setState({ status: 'saved' });
  }, [clearDebounce, reset, responsibleDefaultUserId]);

  return { flush, reloadSavedVersion, retry, state, stopAutosave };
};
